import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Truck, MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export const DeliveryChecker = () => {
  const [pincode, setPincode] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<{
    serviceable: boolean;
    message: string;
    estimatedDeliveryDate?: string;
    estimatedDays?: number;
  } | null>(null);

  const checkDelivery = async () => {
    if (!/^\d{6}$/.test(pincode)) return;
    setChecking(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('check-delivery-estimate', {
        body: { deliveryPincode: pincode, orderValue: 0 },
      });

      if (error) throw error;

      setResult({
        serviceable: data.success && data.serviceable,
        message: data.message || 'Delivery not available to this pincode',
        estimatedDeliveryDate: data.estimatedDeliveryDate,
        estimatedDays: data.estimatedDays,
      });
    } catch {
      setResult({ serviceable: false, message: 'Unable to check delivery. Try again.' });
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="space-y-2 pt-2">
      <div className="flex items-center gap-2">
        <MapPin className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Check Delivery</span>
      </div>
      <div className="flex gap-2">
        <Input
          placeholder="Enter pin code"
          value={pincode}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
            setPincode(val);
            setResult(null);
          }}
          onKeyDown={(e) => e.key === 'Enter' && checkDelivery()}
          className="max-w-[160px] h-9 text-sm"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={checkDelivery}
          disabled={pincode.length !== 6 || checking}
          className="h-9"
        >
          {checking ? 'Checking...' : 'Check'}
        </Button>
      </div>
      {result && (
        <div className={`flex items-start gap-2 text-sm ${result.serviceable ? 'text-green-700 dark:text-green-400' : 'text-destructive'}`}>
          <Truck className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p>{result.message}</p>
            {result.serviceable && result.estimatedDeliveryDate && (
              <p className="text-xs text-muted-foreground">
                Expected by {new Date(result.estimatedDeliveryDate).toLocaleDateString('en-IN', {
                  weekday: 'short', month: 'short', day: 'numeric',
                })}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
